import type { Patient, Recipient } from "src/types";

type MiniPatient = NonNullable<Recipient["patients"]>[number];

export type BenefitedDisplay =
    | { kind: "none" }
    | { kind: "one"; patient: MiniPatient }
    | { kind: "many"; patients: MiniPatient[] };

/** Patients who receive care / are billed (not the account holder). */
export function benefitedPatientsForRecipient(recipient: Recipient): BenefitedDisplay {
    const all = recipient.patients ?? [];
    const linkedId = recipient.sameAsPatientId?.trim() ?? "";

    if (linkedId) {
        const selfPatient = all.find((p) => p.id === linkedId);
        const dependents = all.filter((p) => p.id !== linkedId);
        if (dependents.length === 1) return { kind: "one", patient: dependents[0] };
        if (dependents.length > 1) return { kind: "many", patients: dependents };
        if (selfPatient) return { kind: "one", patient: selfPatient };
    }

    if (all.length === 1) return { kind: "one", patient: all[0] };
    if (all.length > 1) return { kind: "many", patients: all };
    return { kind: "none" };
}

export function recipientIsSelfPayer(recipient: Recipient): boolean {
    const linkedId = recipient.sameAsPatientId?.trim() ?? "";
    if (!linkedId) return false;
    const dependents = (recipient.patients ?? []).filter((p) => p.id !== linkedId);
    return dependents.length === 0;
}

export function personLabel(
    person: Pick<Patient, "firstName" | "lastName">,
): string {
    return `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim();
}

/** True if query matches the account holder or any linked (sub-)patient. */
export function recipientMatchesSearch(recipient: Recipient, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;

    const name = personLabel(recipient).toLowerCase();
    const email = (recipient.email ?? "").toLowerCase();
    const tels = (recipient.tel ?? []).some((t) => t.toLowerCase().includes(q));
    const addr = `${recipient.street ?? ""} ${recipient.plz ?? ""} ${recipient.city ?? ""}`.toLowerCase();

    if (name.includes(q) || email.includes(q) || tels || addr.includes(q)) return true;

    return (recipient.patients ?? []).some((p) => {
        const patientName = personLabel(p).toLowerCase();
        const patientEmail = (p.email ?? "").toLowerCase();
        const patientTels = (p.tel ?? []).some((t) => t.toLowerCase().includes(q));
        const patientAddr = `${p.street ?? ""} ${p.plz ?? ""} ${p.city ?? ""}`.toLowerCase();
        return (
            patientName.includes(q) ||
            patientEmail.includes(q) ||
            patientTels ||
            patientAddr.includes(q)
        );
    });
}
